import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { ApiError } from '@/lib/api';
import { applyPromotion, checkPromotion } from '@/lib/promotion-api';
import {
  type CascadeGroup,
  alsoViaLatestOf,
  cascadeGroups,
  citationsOf,
  confirmLabelOf,
  groupSummary,
  reasonOf,
  summaryOf,
  whatOf,
  whyOf,
} from '@/lib/promotion-labels';
import { VERSION_TAG_LABEL, type VersionedWording, versionedDay } from '@/lib/versioned-labels';
import type {
  PromotionBox,
  PromotionBoxAsset,
  PromotionBoxLine,
  PromotionGesture,
  VersionSummary,
} from '@matter_extractor/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * The box of the cascade (vitrine.md §4.3, §4.4, issue #192; maquette `passage-en-prod.html`,
 * variant B): « Passer en production » on a server that has a prod linked.
 *
 * ## It shows the verdict of the prod BEFORE the click
 *
 * Opening it asks the atelier to JUDGE the gesture at blank: the atelier computes the cascade -
 * everything the version plays, down to the leaves - and has the prod judge it with its own code.
 * The box then says, in a summary line and in groups folded by kind, what would go, how each
 * element is cited, what the prod holds already and who else the move changes; a group that
 * carries a refusal opens by itself and names the reason. The confirm button says how many
 * elements go, and is inert unless every one passes: a cascade goes whole or not at all.
 *
 * ## Four states, and a fifth once it is done
 *
 * « La prod vérifie » while the verdict is awaited; « tout passe »; « refus » - the button inert
 * with its reason; « prod injoignable » - nothing changed anywhere, « Réessayer ». Once the passage
 * went through, the box says what the prod now plays and what the atelier wrote, and only offers
 * to close.
 *
 * It replaces the plain confirmation of the pointer box where a prod is linked (`PassageBox`);
 * with no prod, that confirmation stays, as it was.
 */

export interface CascadeDialogProps {
  /**
   * Only what the box words with: the test-id prefix and the subject. Narrower than the whole
   * `VersionedWording` so the screen « État de la prod » can open it for a kind with no editor yet.
   */
  wording: Pick<VersionedWording, 'entity' | 'subject'>;
  name: string;
  /** The gesture: a passage, or the move back of « latest prod » (§4.1). */
  gesture: Extract<PromotionGesture, { type: 'promote' | 'pointer' }>;
  target: VersionSummary;
  /** Where « latest prod » sits today, or null when nothing was ever passed. */
  current: VersionSummary | null;
  /** The passage went through: the caller re-reads everything the cascade moved. */
  onPassed: () => void;
  onCancel: () => void;
}

const ROW =
  'flex flex-col gap-0.5 border-gray-100 border-t px-3 py-2 pl-6 text-sm first:border-t-0';

function Chip({ kind, children }: { kind: 'geste' | 'lat' | 'pin'; children: string }) {
  const tone =
    kind === 'lat'
      ? 'bg-bleu-nuit-tint text-bleu-nuit'
      : kind === 'pin'
        ? 'bg-gray-100 text-gray-700'
        : 'border border-gray-300 text-gray-700';
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-sm px-1.5 font-semibold text-xs leading-5 ${tone}`}
    >
      {children}
    </span>
  );
}

function Refusal({ reason }: { reason: string }) {
  return (
    <p
      data-testid="cascade-refus"
      className="mt-1 flex flex-wrap items-baseline gap-2 rounded-md bg-rouge-tint px-2 py-1.5 font-semibold text-rouge-deep text-sm"
    >
      <span className="rounded-sm bg-rouge-deep px-2 font-semibold text-white text-xs leading-5">
        refusé
      </span>
      <span>{reason}</span>
    </p>
  );
}

function VersionRow({ line }: { line: PromotionBoxLine }) {
  const also = alsoViaLatestOf(line);
  return (
    <li data-testid="cascade-ligne" className={ROW}>
      <span className="flex flex-wrap items-center gap-2">
        <span>
          <b className="font-semibold">{line.entityName}</b> v{line.versionNumber}
        </span>
        {citationsOf(line).map((cite) => (
          <Chip key={cite.text} kind={cite.kind}>
            {cite.text}
          </Chip>
        ))}
        {line.citedBy.length === 0 ? null : (
          <span className="text-gray-600 text-xs">
            par {line.citedBy.map((cited) => `${cited.name} v${cited.number}`).join(', ')}
          </span>
        )}
      </span>
      <span className={line.status === 'already' && !line.advancesPointer ? 'text-gray-600' : ''}>
        <span className={line.status === 'sends' ? 'font-semibold text-gray-900' : ''}>
          {whatOf(line)}
        </span>
      </span>
      {also === null ? null : (
        <span className="text-vert-deep text-xs">↳ aussi en production via latest : {also}</span>
      )}
      {line.status === 'refused' ? <Refusal reason={reasonOf(line.reason)} /> : null}
    </li>
  );
}

function AssetRow({ asset }: { asset: PromotionBoxAsset }) {
  return (
    <li data-testid="cascade-ligne" className={ROW}>
      <span className="flex flex-wrap items-center gap-2">
        <b className="font-semibold">{asset.name}</b>
        {asset.citedBy.length === 0 ? null : (
          <span className="text-gray-600 text-xs">
            cité par {asset.citedBy.map((cited) => `${cited.name} v${cited.number}`).join(', ')}
          </span>
        )}
      </span>
      <span
        className={asset.status === 'already' ? 'text-gray-600' : 'font-semibold text-gray-900'}
      >
        {asset.status === 'already'
          ? 'déjà sur la prod : rien à envoyer'
          : 'part : le fichier est absent de la prod'}
      </span>
      {asset.status === 'refused' ? <Refusal reason={reasonOf(asset.reason)} /> : null}
    </li>
  );
}

function Group({ group }: { group: CascadeGroup }) {
  const refused = group.refused > 0;
  return (
    <details
      data-testid={`cascade-groupe-${group.id}`}
      // A group that carries a refusal opens by itself (§4.3): the reason is where the eye lands.
      open={refused ? true : undefined}
      className={`rounded-md border ${refused ? 'border-rouge-deep' : 'border-gray-200'}`}
    >
      <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-2 gap-y-0.5 px-3 py-2">
        <b className="font-semibold text-sm">{group.title}</b>
        <span className="text-gray-600 text-xs">
          {[group.subtitle, groupSummary(group)]
            .filter((part) => part !== null && part !== '')
            .join(' · ')}
        </span>
        {refused ? (
          <span className="font-bold text-rouge-deep text-xs">{group.refused} refus</span>
        ) : null}
      </summary>
      <ul>
        {group.rows.map((row) =>
          row.type === 'version' ? (
            <VersionRow
              key={`${row.line.kind}-${row.line.entityId}-${row.line.versionNumber}`}
              line={row.line}
            />
          ) : (
            <AssetRow key={row.asset.id} asset={row.asset} />
          ),
        )}
      </ul>
    </details>
  );
}

export function CascadeDialog({
  wording,
  name,
  gesture,
  target,
  current,
  onPassed,
  onCancel,
}: CascadeDialogProps) {
  // « Réessayer » asks again: a new key, a new judgment, nothing cached.
  const [attempt, setAttempt] = useState(0);
  const check = useQuery({
    queryKey: ['promotion-check', gesture, attempt],
    queryFn: () => checkPromotion(gesture),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const apply = useMutation({
    mutationFn: () => applyPromotion(gesture),
    onSuccess: (box) => {
      if (box.applied) onPassed();
    },
  });

  const box: PromotionBox | null = apply.data ?? check.data ?? null;
  const checking = check.isPending && apply.data === undefined;
  const applied = apply.data?.applied === true;
  const failed = check.error ?? apply.error;
  const state = checking
    ? 'checking'
    : failed !== null && box === null
      ? 'unreachable'
      : (box?.state ?? 'unreachable');
  const goes = box === null ? null : box;
  const moveBack = gesture.type === 'pointer';
  const label = VERSION_TAG_LABEL.production;

  const retry = () => {
    apply.reset();
    setAttempt((value) => value + 1);
  };

  const errorText =
    failed instanceof ApiError
      ? failed.message
      : failed instanceof Error
        ? 'La prod n’a pas pu être jointe depuis l’atelier.'
        : null;

  const groups = goes === null ? [] : cascadeGroups(goes);
  const unreachable = state === 'unreachable';
  const prodCode = box?.prodCode ?? null;

  return (
    <VersionedDialog
      data-testid={`${wording.entity}-boite-cascade`}
      title={
        moveBack
          ? `Reculer ${label} sur la v${target.number}`
          : `Passer ${name} v${target.number} en production`
      }
      description={
        moveBack
          ? `${wording.subject(name)}. Le pointeur ${label} quitte la v${current?.number ?? '?'} pour la v${target.number} ; rien n’est copié. La prod le fait d’abord.`
          : `${wording.subject(name)}. La v${target.number} part sur la prod avec tout ce qu’elle cite. La prod juge d’abord toute la cascade avec son propre code${prodCode === null ? '' : ` (${prodCode})`}, puis l’applique d’un bloc : tout ou rien. Le dev ne bouge qu’après.`
      }
      confirmLabel={confirmLabelOf(gesture, state === 'ok' ? goes : null)}
      busy={apply.isPending}
      error={applied ? null : unreachable ? null : apply.error instanceof Error ? errorText : null}
      confirmDisabled={state !== 'ok'}
      confirmHidden={applied}
      cancelLabel={applied ? 'Fermer' : 'Annuler'}
      note={applied ? null : whyOf(state)}
      onConfirm={() => apply.mutate()}
      onCancel={onCancel}
    >
      <VersionedDialogSection title={`Aujourd’hui en ${label}`}>
        {current === null ? (
          <p className="text-gray-500 text-sm italic">Rien n’a jamais été passé en production.</p>
        ) : (
          <p className="text-gray-800 text-sm">
            v{current.number}, depuis le {versionedDay(current.createdAt)}{' '}
            <span className="text-gray-600">
              · {current.createdBy}
              {current.note === null || current.note === '' ? '' : ` · « ${current.note} »`}
            </span>
          </p>
        )}
      </VersionedDialogSection>

      <VersionedDialogSection
        title={moveBack ? 'Ce que la prod joue ensuite' : 'Ce qui part avec elle'}
      >
        <div className="space-y-2" data-testid="cascade">
          {unreachable ? (
            <div
              role="alert"
              data-testid="cascade-injoignable"
              className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-rouge-tint px-3 py-2.5 font-semibold text-rouge-deep text-sm"
            >
              <span>
                {box?.message ?? errorText ?? 'La prod ne répond pas.'} Rien n’a changé, ni sur la
                prod ni sur le dev.
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="cascade-reessayer"
                onClick={retry}
              >
                Réessayer
              </Button>
            </div>
          ) : null}

          {checking ? (
            <p
              data-testid="cascade-verifie"
              className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2 font-semibold text-sm"
            >
              <span
                aria-hidden="true"
                className="size-3 shrink-0 animate-spin rounded-full border-2 border-bleu-ciel-tint border-t-bleu-ciel-deep motion-reduce:animate-none"
              />
              La prod juge la cascade…
            </p>
          ) : null}

          {goes !== null && !unreachable && !checking ? (
            <p
              data-testid="cascade-resume"
              className={`rounded-md px-3 py-2 font-semibold text-sm ${state === 'refused' ? 'bg-rouge-tint text-rouge-deep' : state === 'ok' ? 'bg-vert-tint text-vert-deep' : 'bg-gray-50'}`}
            >
              {summaryOf(goes)}
            </p>
          ) : null}

          {goes?.gestureRefusal === undefined ? null : (
            <Refusal reason={reasonOf(goes.gestureRefusal)} />
          )}

          {!checking && !unreachable
            ? groups.map((group) => <Group key={`${group.id}-${state}`} group={group} />)
            : null}

          {applied ? (
            <output
              data-testid="cascade-fait"
              className="block rounded-md bg-vert-tint px-3 py-2.5 text-sm text-vert-deep"
            >
              Appliqué sur la prod : elle joue {name} v{target.number}. Sur le dev, {label} passe à
              la v{target.number}
              {goes === null
                ? '.'
                : ` et les ${goes.lines.filter((line) => line.status !== 'already').length} versions envoyées portent la marque « en prod ».`}
            </output>
          ) : null}
        </div>
      </VersionedDialogSection>

      <Callout tone="neutral" title="Les sessions en cours ne changent pas">
        Chacune garde les versions gelées à son démarrage. Seules les sessions qui démarrent après
        ce geste jouent la v{target.number}.
      </Callout>
    </VersionedDialog>
  );
}
